import * as THREE from "three";
import {
    getAdjacencyListFromMesh,
    getPointProjectedOntoPlane,
    getRaycasterIntersectionWithPlane,
} from "../../helpers";
import StructureModel from "../PatientAnatomy/StructureModel";
import ProstateAxes from "../Other/ProstateAxes";
import { volumeNames } from "../../../../../constants";
import { laplacianFilter, laplacianHCFilter } from "./LaplacianFilter";
import { RAS2LPS, LPS2RAS } from "../../helpers";
import MeshEditUndoStack from "./MeshEditUndoStack";

const DEFAULT_MAX_UPDATE_DISTANCE = 15;
const MAX_MAX_UPDATE_DISTANCE = 25;
const MIN_MAX_UPDATE_DISTANCE = 5;
const MOUSE_DELTA_SCALE = 20;
const LAPLACIAN_ITERATIONS = 3;

export default class MeshEditController {
    constructor(mriModel) {
        this._mriModel = mriModel;
        this._maxUpdateDistance = DEFAULT_MAX_UPDATE_DISTANCE;
        this._undoStack = new MeshEditUndoStack();
    }

    get linkedStructure() {
        return this._linkedStructure;
    }

    set linkedStructure(structure) {
        if (!structure) {
            return;
        }

        this._linkedStructure = structure;
    }

    get editedStructure() {
        return this._editStructure;
    }

    get faceVertexRepresentation() {
        return {
            vtxPts: this._editStructure.geometry.attributes.array,
            triIdx: this._editStructure.geometry.index.array,
        };
    }

    get editingActive() {
        return !(this._editStructure == null);
    }

    get maxUpdateDistance() {
        return this._maxUpdateDistance;
    }

    set maxUpdateDistance(value) {
        this._maxUpdateDistance = Math.max(
            Math.min(value, MAX_MAX_UPDATE_DISTANCE),
            MIN_MAX_UPDATE_DISTANCE
        );
    }

    get canUndo() {
        return this._undoStack.canUndo;
    }

    get canRedo() {
        return this._undoStack.canRedo;
    }

    set onEditEnd(callback) {
        this._onEditEnd = callback;
    }

    cancelEditing() {
        if (this._editStructure) {
            this._editStructure.delete();
            this._editStructure = null;
        }

        if (this._linkedStructure) {
            this._linkedStructure.mriViewer.mount.style.cursor = "default";
        }

        this._undoStack.clear();
    }

    initializeEditing() {
        if (!this._linkedStructure) {
            return;
        }

        let clonedGeometry = this._linkedStructure.geometry.clone();

        const highlighted = new Array(
            clonedGeometry.attributes.position.count
        ).fill(0);
        clonedGeometry.setAttribute(
            "highlighted",
            new THREE.BufferAttribute(new Float32Array(highlighted), 1)
        );

        this._editStructure = new StructureModel();
        this._editStructure.mriViewer = this._linkedStructure.mriViewer;
        this._editStructure.meshViewer = this._linkedStructure.meshViewer;
        this._editStructure.initializeViewFromGeometry(
            clonedGeometry,
            {
                color: "#FFFFFF",
                opacity: 0.3,
                name: `${this._linkedStructure.name}${volumeNames.EDITING}`,
                editingActive: true,
            },
            clonedGeometry.clone()
        );
        let prostateAxes = new ProstateAxes();
        this._editStructure.initProstateAxes(prostateAxes);
        this._editStructure.visibility = true;
        this._linkedStructure.visibility = false;
    }

    selectVertex(raycaster) {
        const mousePlaneIntersectLPS = getRaycasterIntersectionWithPlane(
            raycaster,
            this._mriModel.clipPlane
        ).applyMatrix4(RAS2LPS);
        const verticesLPS =
            this._editStructure.geometry.attributes.position.array;
        this._vertexIndicesToMove = new Map();
        this._vertexIndicesToSmooth = new Set();
        this._selectedVertexIndex = null;
        let selectedVertexDistance = Infinity;

        const numVertices = Math.round(verticesLPS.length / 3);
        for (let i = 0; i < numVertices; i++) {
            const vertexLPS = new THREE.Vector3(
                verticesLPS[3 * i],
                verticesLPS[3 * i + 1],
                verticesLPS[3 * i + 2]
            );
            const distanceToMousePlaneIntersect = vertexLPS.distanceTo(
                mousePlaneIntersectLPS
            );

            if (
                distanceToMousePlaneIntersect <= this._maxUpdateDistance &&
                distanceToMousePlaneIntersect <= selectedVertexDistance
            ) {
                selectedVertexDistance = distanceToMousePlaneIntersect;
                this._selectedVertexIndex = i;
            }
        }
    }

    updateVerticesToMoveAndSmooth() {
        if (!this._selectedVertexIndex) {
            return;
        }

        this._editStructure.geometry.computeBoundingBox();
        this._editStructure.geometry.computeBoundingSphere();
        this._editStructure.geometry.computeBoundsTree();

        const verticesLPS =
            this._editStructure.geometry.attributes.position.array;
        const adj = getAdjacencyListFromMesh(this._editStructure.faces);
        const selectedVertexLPS = new THREE.Vector3(
            verticesLPS[3 * this._selectedVertexIndex],
            verticesLPS[3 * this._selectedVertexIndex + 1],
            verticesLPS[3 * this._selectedVertexIndex + 2]
        );
        const planeLPS = this._mriModel.clipPlane.clone().applyMatrix4(RAS2LPS);
        const projectedCenterLPS = getPointProjectedOntoPlane(
            this._editStructure.center,
            planeLPS
        );
        const projectedCenterToSelectedVertex = getPointProjectedOntoPlane(
            selectedVertexLPS,
            planeLPS
        )
            .sub(projectedCenterLPS)
            .normalize();
        this._vertexIndicesToMove = new Map();
        this._vertexIndicesToSmooth = new Set();

        for (let i = 0; i < verticesLPS.length; i += 3) {
            const vertexLPS = new THREE.Vector3(
                verticesLPS[i],
                verticesLPS[i + 1],
                verticesLPS[i + 2]
            );
            const projectedCenterToVertex = getPointProjectedOntoPlane(
                vertexLPS,
                planeLPS
            )
                .sub(projectedCenterLPS)
                .normalize();
            const distanceToSelectedVertex =
                vertexLPS.distanceTo(selectedVertexLPS);

            if (
                distanceToSelectedVertex <= this._maxUpdateDistance &&
                projectedCenterToSelectedVertex.dot(projectedCenterToVertex) > 0
            ) {
                const vertexIndex = Math.floor(i / 3);

                this._vertexIndicesToMove.set(
                    vertexIndex,
                    (this._maxUpdateDistance - distanceToSelectedVertex) /
                        this._maxUpdateDistance
                );

                this._vertexIndicesToSmooth.add(vertexIndex);

                const neighbors = adj[vertexIndex];

                if (!neighbors) {
                    continue;
                }

                for (const neighbor of neighbors) {
                    this._vertexIndicesToSmooth.add(neighbor);
                }
            }
        }
    }

    move(mouseDelta) {
        if (!this._vertexIndicesToMove) {
            return;
        }

        const scale = MOUSE_DELTA_SCALE / this._mriModel.mriViewer.camera.zoom;
        const upDeltaLPS = new THREE.Vector3()
            .setFromMatrixColumn(this._mriModel.mriViewer.camera.matrixWorld, 1)
            .applyMatrix4(RAS2LPS)
            .multiplyScalar(scale * mouseDelta.y);
        const rightDeltaLPS = new THREE.Vector3()
            .setFromMatrixColumn(this._mriModel.mriViewer.camera.matrixWorld, 0)
            .applyMatrix4(RAS2LPS)
            .multiplyScalar(scale * mouseDelta.x);
        const verticesLPS =
            this._editStructure.geometry.attributes.position.array;

        for (const [vertexIndex, distanceScale] of this._vertexIndicesToMove) {
            const vertexLPS = new THREE.Vector3(
                verticesLPS[3 * vertexIndex],
                verticesLPS[3 * vertexIndex + 1],
                verticesLPS[3 * vertexIndex + 2]
            );

            vertexLPS.add(upDeltaLPS.clone().multiplyScalar(distanceScale));
            vertexLPS.add(rightDeltaLPS.clone().multiplyScalar(distanceScale));

            verticesLPS[3 * vertexIndex] = vertexLPS.x;
            verticesLPS[3 * vertexIndex + 1] = vertexLPS.y;
            verticesLPS[3 * vertexIndex + 2] = vertexLPS.z;
        }

        for (let i = 0; i < verticesLPS.length; i++) {
            this._editStructure.meshViewGeometry.attributes.position.array[i] =
                verticesLPS[i];
        }

        this._editStructure.geometry.attributes.position.needsUpdate = true;
        this._editStructure.meshViewGeometry.attributes.position.needsUpdate = true;
    }

    highlight() {
        const verticesLPS =
            this._editStructure.geometry.attributes.position.array;

        if (!this._selectedVertexIndex) {
            for (let i = 0; i < Math.round(verticesLPS.length / 3); i++) {
                this._editStructure.geometry.attributes.highlighted.array[i] =
                    0;
                this._editStructure.meshViewGeometry.attributes.highlighted.array[
                    i
                ] = 0;
            }
        } else {
            this._editStructure.geometry.computeBoundingBox();
            this._editStructure.geometry.computeBoundingSphere();
            this._editStructure.geometry.computeBoundsTree();

            const selectedVertexRAS = new THREE.Vector3(
                verticesLPS[3 * this._selectedVertexIndex],
                verticesLPS[3 * this._selectedVertexIndex + 1],
                verticesLPS[3 * this._selectedVertexIndex + 2]
            ).applyMatrix4(LPS2RAS);
            const selectedVertexNDC = selectedVertexRAS
                .clone()
                .project(this._mriModel.mriViewer.camera);
            const raycaster = new THREE.Raycaster();
            const projectedCenterRAS = getPointProjectedOntoPlane(
                this._editStructure.center.clone().applyMatrix4(LPS2RAS),
                this._mriModel.clipPlane
            );
            const projectedCenterToSelectedVertexRAS =
                getPointProjectedOntoPlane(
                    selectedVertexRAS,
                    this._mriModel.clipPlane
                )
                    .sub(projectedCenterRAS)
                    .normalize();

            // We just need the ray here; there's definitely a way to do the math
            // without a Raycaster but...
            raycaster.setFromCamera(
                new THREE.Vector2(selectedVertexNDC.x, selectedVertexNDC.y),
                this._mriModel.mriViewer.camera
            );

            for (let i = 0; i < Math.round(verticesLPS.length / 3); i++) {
                const vertexRAS = new THREE.Vector3(
                    verticesLPS[3 * i],
                    verticesLPS[3 * i + 1],
                    verticesLPS[3 * i + 2]
                ).applyMatrix4(LPS2RAS);
                const projectedCenterToVertexRAS = getPointProjectedOntoPlane(
                    vertexRAS,
                    this._mriModel.clipPlane
                )
                    .sub(projectedCenterRAS)
                    .normalize();

                const shouldHighlightMRI =
                    raycaster.ray.distanceToPoint(vertexRAS) <=
                        this._maxUpdateDistance &&
                    projectedCenterToSelectedVertexRAS.dot(
                        projectedCenterToVertexRAS
                    ) > 0;
                this._editStructure.geometry.attributes.highlighted.array[i] =
                    shouldHighlightMRI ? 1 : 0;

                const shouldHighlightMesh =
                    selectedVertexRAS.distanceTo(vertexRAS) <=
                    this._maxUpdateDistance;
                this._editStructure.meshViewGeometry.attributes.highlighted.array[
                    i
                ] = shouldHighlightMesh ? 1 : 0;
            }
        }

        this._editStructure.geometry.attributes.highlighted.needsUpdate = true;
        this._editStructure.meshViewGeometry.attributes.highlighted.needsUpdate = true;
    }

    smooth() {
        if (!this._vertexIndicesToSmooth) {
            return;
        }

        const origVertsLPS = Array.from(
            this._editStructure.geometry.attributes.position.array
        );
        const currVertsLPS =
            this._editStructure.geometry.attributes.position.array;
        const adj = getAdjacencyListFromMesh(this._editStructure.faces);

        for (let i = 0; i < LAPLACIAN_ITERATIONS; i++) {
            const prevVertsLPS = Array.from(currVertsLPS);

            laplacianFilter(
                currVertsLPS,
                prevVertsLPS,
                this._vertexIndicesToSmooth,
                adj
            );
            laplacianHCFilter(
                origVertsLPS,
                currVertsLPS,
                prevVertsLPS,
                this._vertexIndicesToSmooth,
                adj
            );
        }

        for (let i = 0; i < currVertsLPS.length; i++) {
            this._editStructure.meshViewGeometry.attributes.position.array[i] =
                currVertsLPS[i];
        }

        this._editStructure.geometry.attributes.position.needsUpdate = true;
        this._editStructure.meshViewGeometry.attributes.position.needsUpdate = true;
    }

    updateCursor(moving) {
        this._linkedStructure.mriViewer.mount.style.cursor = moving
            ? "pointer"
            : "default";
    }

    startEdit() {
        if (this._edit) {
            return;
        }

        this._edit = Array.from(
            this._editStructure.geometry.attributes.position.array
        );
    }

    endEdit() {
        if (!this._edit) {
            return;
        }

        for (let i = 0; i < this._edit.length; i++) {
            this._edit[i] =
                this._editStructure.geometry.attributes.position.array[i] -
                this._edit[i];
        }

        this._undoStack.edit(this._edit);

        this._edit = undefined;

        if (this._onEditEnd) {
            this._onEditEnd();
        }
    }

    undo() {
        if (!this._undoStack.canUndo) {
            return;
        }

        const deltas = this._undoStack.undo();

        for (
            let i = 0;
            i < this._editStructure.geometry.attributes.position.array.length;
            i++
        ) {
            this._editStructure.geometry.attributes.position.array[i] -=
                deltas[i];
            this._editStructure.meshViewGeometry.attributes.position.array[i] -=
                deltas[i];
        }

        this._editStructure.geometry.attributes.position.needsUpdate = true;
        this._editStructure.meshViewGeometry.attributes.position.needsUpdate = true;
    }

    redo() {
        if (!this._undoStack.canRedo) {
            return;
        }

        const deltas = this._undoStack.redo();

        for (
            let i = 0;
            i < this._editStructure.geometry.attributes.position.array.length;
            i++
        ) {
            this._editStructure.geometry.attributes.position.array[i] +=
                deltas[i];
            this._editStructure.meshViewGeometry.attributes.position.array[i] +=
                deltas[i];
        }

        this._editStructure.geometry.attributes.position.needsUpdate = true;
        this._editStructure.meshViewGeometry.attributes.position.needsUpdate = true;
    }

    clearUndoStack() {
        this._undoStack.clear();
    }
}
